import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import {
  getMyEnrollments,
  getMyCertificates,
  getUpcomingWorkshops,
} from '../../api/client';
import LoadingScreen from '../../components/layout/LoadingScreen';
import Currency from '../../components/Currency';
import type { Enrollment } from '../../types/course';
import {
  BookOpen,
  Award,
  Clock,
  ArrowRight,
  Play,
  TrendingUp,
  CheckCircle2,
  Calendar,
  MapPin,
  Zap,
  GraduationCap,
  User,
  BarChart3,
} from 'lucide-react';

interface Certificate {
  id: number;
  certificate_number: string;
  course_name: string;
  completion_date: string;
  status: string;
}

interface Workshop {
  id: number;
  title: string;
  description?: string;
  short_description?: string;
  start_date?: string;
  start_time?: string;
  duration?: string;
  price?: number;
  status?: string;
  meeting_link?: string;
}

const focusClasses = 'focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2';

function StatCard({
  icon: Icon,
  label,
  value,
  color,
  delay,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  color: string;
  delay: number;
}): React.JSX.Element {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      initial={reducedMotion ? undefined : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-lg hover:border-amber-200 transition-all"
    >
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 font-medium mt-0.5">{label}</p>
    </motion.div>
  );
}

function ProgressBar({ progress, color = 'bg-amber-500' }: { progress: number; color?: string }): React.JSX.Element {
  return (
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} rounded-full transition-all duration-500`}
        style={{ width: `${Math.min(progress, 100)}%` }}
      />
    </div>
  );
}

export default function DashboardScreen(): React.JSX.Element {
  const { user } = useAuth();
  const toast = useToast();
  const reducedMotion = useReducedMotion();

  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [enrollRes, certRes, wsRes] = await Promise.all([
          getMyEnrollments(),
          getMyCertificates().catch(() => ({ certificates: [] })),
          getUpcomingWorkshops().catch(() => ({ workshops: [] })),
        ]);
        setEnrollments(enrollRes.enrollments || []);
        setCertificates(certRes.certificates || []);
        setWorkshops((wsRes as any).workshops || []);
      } catch (error: any) {
        toast.show(error.message || 'Failed to load dashboard', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingScreen message="Loading your dashboard..." />;

  const inProgress = enrollments.filter((e) => e.status === 'active');
  const completed = enrollments.filter((e) => e.status === 'completed');
  const pending = enrollments.filter((e) => e.status === 'pending');

  const firstName = user?.name?.split(' ')[0] || 'Learner';

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 animate-fade-in">
      {/* Welcome Header */}
      <motion.div
        initial={reducedMotion ? undefined : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
      >
        <div className="flex items-center gap-4">
          {user?.avatar ? (
            <img src={user.avatar} alt="" className="w-14 h-14 rounded-2xl object-cover border-2 border-white shadow-md" />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center text-white text-xl font-bold shadow-md">
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Welcome back, {firstName}
            </h1>
            <p className="text-sm text-slate-500">
              Here's what's happening with your learning journey.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/courses"
            className={`px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20 flex items-center gap-2 ${focusClasses}`}
          >
            <Zap className="w-4 h-4" /> Explore Courses
          </Link>
          <Link
            to="/workshops"
            className={`px-4 py-2 border-2 border-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:border-amber-500 hover:text-amber-600 transition-colors ${focusClasses}`}
          >
            Workshops
          </Link>
        </div>
      </motion.div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={BookOpen} label="Enrolled" value={enrollments.length} color="bg-sky-500" delay={0.05} />
        <StatCard icon={TrendingUp} label="In Progress" value={inProgress.length} color="bg-amber-500" delay={0.1} />
        <StatCard icon={CheckCircle2} label="Completed" value={completed.length} color="bg-emerald-500" delay={0.15} />
        <StatCard icon={Award} label="Certificates" value={certificates.length} color="bg-violet-500" delay={0.2} />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* LEFT COLUMN — Learning */}
        <div className="lg:col-span-2 space-y-8">
          {/* Continue Learning */}
          {inProgress.length > 0 && (
            <section aria-label="Continue learning">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Play className="w-5 h-5 text-amber-500" />
                  Continue Learning
                </h2>
                <Link to="/my-courses" className="text-sm font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="space-y-3">
                {inProgress.slice(0, 3).map((item) => (
                  <Link
                    key={item.id}
                    to={`/courses/${item.course_slug}`}
                    className={`group flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-100 hover:shadow-md hover:border-amber-200 transition-all ${focusClasses}`}
                  >
                    {item.course_image ? (
                      <img src={item.course_image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" loading="lazy" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-6 h-6 text-amber-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 text-sm truncate group-hover:text-amber-600 transition-colors">
                        {item.course_title}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {item.progress_percent || 0}% complete
                      </p>
                      <div className="mt-2">
                        <ProgressBar progress={item.progress_percent || 0} />
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-amber-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* My Courses */}
          <section aria-label="My courses">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-slate-700" />
                My Courses
              </h2>
              <Link to="/my-courses" className="text-sm font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {enrollments.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
                <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-900 mb-1">No courses yet</h3>
                <p className="text-sm text-slate-500 mb-4">Start your learning journey today.</p>
                <Link
                  to="/courses"
                  className={`inline-flex items-center gap-2 px-5 py-2 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors ${focusClasses}`}
                >
                  Browse Courses <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {enrollments.slice(0, 4).map((item) => (
                  <Link
                    key={item.id}
                    to={`/courses/${item.course_slug}`}
                    className={`group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-md hover:border-amber-200 transition-all ${focusClasses}`}
                  >
                    <div className="flex items-center gap-3 p-4">
                      {item.course_image ? (
                        <img src={item.course_image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-6 h-6 text-amber-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-900 text-sm truncate group-hover:text-amber-600 transition-colors">
                          {item.course_title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            item.status === 'completed'
                              ? 'bg-emerald-50 text-emerald-700'
                              : item.status === 'active'
                              ? 'bg-sky-50 text-sky-700'
                              : 'bg-amber-50 text-amber-700'
                          }`}>
                            {item.status}
                          </span>
                          {item.progress_percent !== undefined && item.status !== 'completed' && (
                            <span className="text-xs text-slate-400">{item.progress_percent}%</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {item.progress_percent !== undefined && item.status !== 'completed' && (
                      <div className="px-4 pb-4">
                        <ProgressBar progress={item.progress_percent} />
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* RIGHT COLUMN — Workshops & Quick Links */}
        <div className="space-y-8">
          {/* Upcoming Workshops */}
          <section aria-label="Upcoming workshops">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-700" />
                Workshops
              </h2>
              <Link to="/workshops" className="text-sm font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1">
                All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {workshops.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
                <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No upcoming workshops</p>
              </div>
            ) : (
              <div className="space-y-3">
                {workshops.slice(0, 4).map((w) => (
                  <Link
                    key={w.id}
                    to={`/workshops/${w.id}`}
                    className={`group block bg-white rounded-2xl border border-slate-100 p-4 hover:shadow-md hover:border-amber-200 transition-all ${focusClasses}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-slate-900 text-sm group-hover:text-amber-600 transition-colors line-clamp-1">
                        {w.title}
                      </h3>
                      <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0">
                        {w.status === 'upcoming' ? 'Upcoming' : 'Open'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 mb-2">
                      {w.description || w.short_description || 'Hands-on workshop led by industry experts.'}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        {w.start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(w.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                        {w.start_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {w.start_time}
                          </span>
                        )}
                      </div>
                      <Currency value={w.price || 0} className="text-xs font-bold text-slate-700" showFree />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Quick Links */}
          <section aria-label="Quick links">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-slate-700" />
              Quick Actions
            </h2>
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              {[
                { icon: BookOpen, label: 'My Courses', desc: 'View all enrollments', to: '/my-courses' },
                { icon: Award, label: 'Certificates', desc: 'Download your certificates', to: '/my-courses' },
                { icon: User, label: 'Profile', desc: 'Update your details', to: '/profile' },
                { icon: Zap, label: 'Mentor', desc: 'AI Career Companion', to: '/mentor' },
              ].map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`group flex items-center gap-3 px-4 py-3 hover:bg-amber-50 transition-colors border-b border-slate-50 last:border-0 ${focusClasses}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                    <item.icon className="w-4 h-4 text-slate-500 group-hover:text-amber-600 transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-amber-700 transition-colors">
                      {item.label}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{item.desc}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
